import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organization.service';
import RouteGateFallback from '../ui/RouteGateFallback';
import { isLandlordOwner } from '../../utils/portalAuth';

const SKIP_PREFIXES = [
  '/org-setup',
  '/force-change-password',
  '/verify-phone',
  '/signup-pending-approval',
  '/accept-invitation',
];

const cacheKey = (userId: string) => `tp:org-onboarding-done:${userId}`;

interface OrganizationOnboardingGateProps {
  children: React.ReactNode;
}

/**
 * Sends OWNER/ADMIN landlords to /org-setup until onboardingCompletedAt is set.
 * Team members (agent/caretaker) are not blocked — owner completes org setup.
 *
 * Does NOT run inside POST /auth/login — only after the session exists.
 * Session-caches a positive "done" result so route changes don't refetch.
 */
const OrganizationOnboardingGate: React.FC<OrganizationOnboardingGateProps> = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [gate, setGate] = useState<'loading' | 'pass' | 'setup'>('loading');

  useEffect(() => {
    if (loading || !isAuthenticated || !user) {
      setGate('loading');
      return;
    }

    const path = location.pathname;
    if (SKIP_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
      setGate('pass');
      return;
    }

    // Portfolio owners / admins need org setup; prefer membership when available later in gate.
    // Invited staff (agent/caretaker without owner flag) skip.
    if (!isLandlordOwner(user) && user.role !== 'ADMIN' && user.role !== 'LANDLORD') {
      setGate('pass');
      return;
    }

    try {
      if (sessionStorage.getItem(cacheKey(user.id)) === '1') {
        setGate('pass');
        return;
      }
    } catch {
      /* private mode */
    }

    let cancelled = false;
    setGate('loading');

    (async () => {
      try {
        const { organization, membership } = await organizationService.getMine();
        if (cancelled) return;
        if (organization.onboardingCompletedAt) {
          try {
            sessionStorage.setItem(cacheKey(user.id), '1');
          } catch {
            /* ignore */
          }
          setGate('pass');
          return;
        }
        const canSetup =
          membership?.role === 'OWNER' || membership?.role === 'ADMIN';
        setGate(canSetup ? 'setup' : 'pass');
      } catch {
        // Fail open: don't trap users if org API is down
        if (!cancelled) setGate('pass');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, isAuthenticated, user, location.pathname]);

  if (loading || gate === 'loading') {
    return <RouteGateFallback variant="auth" message="Checking organization" />;
  }

  if (gate === 'setup') {
    return <Navigate to="/org-setup" replace />;
  }

  return <>{children}</>;
};

export default OrganizationOnboardingGate;
